import type React from 'react';
import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useIntl } from 'react-intl-next';

import { usePreset } from '@atlaskit/editor-core/use-preset';
import type { ViewMode } from '@atlaskit/editor-plugin-editor-viewmode';
import type { ExtractPresetAPI } from '@atlaskit/editor-common/preset';
import type { EditorProps } from '@atlaskit/editor-core';
import type { ExtensionPluginOptions } from '@atlaskit/editor-plugin-extension';
import { usePreviousState } from '@atlaskit/editor-common/hooks';
import { type ContextPanelPluginOptions } from '@atlaskit/editor-plugin-context-panel';
import type { GetPMNodeHeight } from '@atlaskit/editor-common/extensibility';
import type { DiffParams } from '@atlaskit/editor-plugin-show-diff';
import type { LimitedModePluginOptions } from '@atlaskit/editor-plugin-limited-mode';
import type { SyncedBlockProvider } from '@atlaskit/editor-synced-block-provider';

import type { createPageEditorPluginAIOptions } from '@atlassian/editor-plugin-ai/ConfluencePrebuilt';
import type { ReferentialityContext } from '@atlassian/editor-referentiality/types';

import { useIsMentionOnRestrictedPageEligible } from '@confluence/experiment-mention-on-restricted-page';
import type { LoomProviderConfig } from '@confluence/fabric-providers/entry-points/getLoomProvider';
import { fg } from '@confluence/feature-gating';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive';
import { useLoomRecorderEntryPoint } from '@confluence/loom-utils/entry-points/useLoomRecorderEntryPoint';
import {
	type MentionType,
	useMentionsNotifications,
} from '@confluence/mentions-notifications/entry-points/useMentionsNotifications';
import { handleMentionsChanged } from '@confluence/mentions-notifications/entry-points/handleMentionsChanged';
import { useObjectSidebarActions } from '@confluence/object-sidebar-api/entry-points/useObjectSidebar';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags';
import type { EditorAPI } from '@confluence/loom-editor-utils/entry-points/loomEditorUtils-types';
import { useLoomPluginOptions } from '@confluence/loom-editor-utils/entry-points/useLoomPluginOptions';
import { useInitializeLoomPlugin } from '@confluence/loom-editor-utils/entry-points/useInitializeLoomPlugin';
import { useEditorEngagementPlatform } from '@confluence/editor-features/entry-points/useEditorEngagementPlatform';
import { useEditorPluginAIDefinitionsConfig } from '@confluence/editor-features/entry-points/useEditorPluginAIDefinitionsConfig';
import { useEmojiNodeDataProvider } from '@confluence/editor-features/entry-points/useEmojiNodeDataProvider';
import { useEditorPluginAIDefinitionsEnabled } from '@confluence/editor-features/entry-points/useEditorPluginAIDefinitionsEnabled';
import { useEditorPluginAIProactiveConfig } from '@confluence/editor-features/entry-points/useEditorPluginAIProactiveConfig';
import { useEditorPluginAIProactiveEnabled } from '@confluence/editor-features/entry-points/useEditorPluginAIProactiveEnabled';
import { useSelectionExtensionPluginOptions } from '@confluence/editor-features/entry-points/useSelectionExtensionPluginOptions';
import { useIsDesktop } from '@confluence/desktop-native-bridge';
import type { AIGlobalOptIn } from '@confluence/editor-features/entry-points/AIGlobalOptIn';
import type { EditorPluginAIFeedbackHandlersRef } from '@confluence/editor-features/entry-points/EditorPluginAIFeedbackHandlersRef';
import type { FullPageAppearances } from '@confluence/content-appearance/entry-points/contentAppearanceTypes';
import { useIsSidePanelOpen } from '@confluence/side-panel-state-container';
import { type PageMode } from '@confluence/issue-create-side-panel/entry-points/IssueCreateSidePanelContextProvider';
import { getPageMode } from '@confluence/issue-create-side-panel/entry-points/pageModeUtils';
import { useRightSidebarContext } from '@confluence/page-layout-context';
import { useIsEligibleCreateNewWhiteboard } from '@confluence/prompt-competitor-link/entry-points/useIsEligibleCreateNewWhiteboard';

import { useEditorPreferencesProvider } from '../utils/EditorPreferences';

import { EditorLoomButton } from './EditorLoomButton';
import {
	type CreateFullPageEditorPresetProps,
	createFullPageEditorPreset,
} from './createFullPageEditorPresetNext';
export interface FullPageEditorPresetProps extends WithFlagsProps {
	appearance: FullPageAppearances;
	featureFlags: EditorProps['featureFlags'];
	onSave: EditorProps['onSave'];
	linking: EditorProps['linking'];
	disabled?: EditorProps['disabled'];
	contextIdentifierProvider: EditorProps['contextIdentifierProvider'];
	media: EditorProps['media'];
	placeholder: EditorProps['placeholder'];
	emojiProvider: EditorProps['emojiProvider'];
	annotationProviders: EditorProps['annotationProviders'];
	autoformattingProvider: EditorProps['autoformattingProvider'];
	taskDecisionProvider: EditorProps['taskDecisionProvider'];
	collabEditProvider: EditorProps['collabEditProvider'];
	mentionProvider: EditorProps['mentionProvider'];
	collabEdit: EditorProps['collabEdit'];
	primaryToolbarComponents: EditorProps['primaryToolbarComponents'];
	waitForMediaUpload: EditorProps['waitForMediaUpload'];
	mention: EditorProps['mention'];
	allowFindReplace: EditorProps['allowFindReplace'];
	allowExtension: EditorProps['allowExtension'];
	extensionHandlers: EditorProps['extensionHandlers'];
	referentialityContext: ReferentialityContext;
	editorPluginAIOptions: ReturnType<typeof createPageEditorPluginAIOptions>;
	aiGlobalOptIn: {
		status: AIGlobalOptIn['optInStatus'];
		triggerOptInFlow: AIGlobalOptIn['triggerOptInFlow'];
	};
	editorPluginAIFeedbackHandlers: React.RefObject<EditorPluginAIFeedbackHandlersRef>;
	elementBrowser: EditorProps['elementBrowser'];
	quickInsert: EditorProps['quickInsert'];
	setEditorAPI?: (editorAPI?: FullPageEditorPresetAPINext) => void;
	viewMode?: ViewMode;
	hasEditPermission?: boolean;
	requestToEditContent?: () => void;
	hasRequestedEditPermission?: boolean;
	getHeadingLinkPath: () => string | null;
	__rendererExtensionOptions?: ExtensionPluginOptions['__rendererExtensionOptions'];
	__livePage?: boolean;
	contextPanelOptions?: ContextPanelPluginOptions;
	contentId: string | undefined;
	spaceId: string | undefined;
	isAIEnabledForContent: boolean;
	getExtensionHeight?: GetPMNodeHeight;
	contextualFormattingEnabled: boolean;
	isCompanyHub?: boolean;
	blockLinkHashPrefix?: string;
	showDiffPluginParams?: DiffParams;
	syncedBlockProvider?: SyncedBlockProvider;
	disableFloatingToolbar?: boolean;
	disableToolbar?: boolean;
}

export type FullPageEditorPresetAPINext = ExtractPresetAPI<
	ReturnType<typeof useFullPageEditorPreset>
>;

// FIXME: https://product-fabric.atlassian.net/browse/ED-25722
export function useFullPageEditorPreset(props: FullPageEditorPresetProps): any {
	const { showObjectSidebar, hideObjectSidebar, hideObjectSidebarByPanelId } =
		useObjectSidebarActions();

	const isLivePage = useIsCurrentPageLive();
	const { isDesktop } = useIsDesktop();

	const { userId } = useSessionData();
	const [
		{
			deletedMentions,
			addedMentions,
			unrestrictedMentions,
			parentSpaceRestrictedMentions,
			pageRestrictedMentions,
			unrestrictedTasks,
			addedTasks,
			deletedTasks,
			pageRestrictedTasks,
			parentSpaceRestrictedTasks,
		},
		{
			setDeletedMentions,
			setAddedMentions,
			setAddedTasks,
			setDeletedTasks,
			setMentionsAdded,
			setTasksAdded,
		},
	] = useMentionsNotifications();
	const [mentionChanges, setMentionChanges] = useState<MentionType[]>([]);
	const previousEditorProps = usePreviousState(props);
	const isClassicMentionsNotificationsEnrolled = useIsMentionOnRestrictedPageEligible();

	const isLivePageRef = useRef<boolean>(isLivePage);
	isLivePageRef.current = isLivePage;

	const getCurrentIsLivePage = useCallback(() => {
		return isLivePageRef.current;
	}, []);

	const { confluenceEditorPreferencesProvider, editorPreferencesProvider } =
		useEditorPreferencesProvider({
			getCurrentIsLivePage,
		});

	const isSidePanelOpen = useIsSidePanelOpen();
	const isSidePanelOpenRef = useRef<boolean>(isSidePanelOpen);
	isSidePanelOpenRef.current = isSidePanelOpen;

	const rightSidebarContext = useRightSidebarContext();
	const rightSidebarContextRef = useRef(rightSidebarContext);
	rightSidebarContextRef.current = rightSidebarContext;

	const hasEditPermission = props.hasEditPermission || false;
	const hasEditPermissionRef = useRef<boolean>(hasEditPermission);
	hasEditPermissionRef.current = hasEditPermission;

	const pageMode = getPageMode(props.viewMode, props.__livePage, props.hasEditPermission);
	const pageModeRef = useRef<PageMode>(pageMode);
	pageModeRef.current = pageMode;

	useEffect(() => {
		const isMentionNotificationsEnabled =
			isLivePage || (!isLivePage && isClassicMentionsNotificationsEnrolled);

		if (mentionChanges.length && isMentionNotificationsEnabled) {
			handleMentionsChanged({
				mentionChanges,
				deletedMentions,
				addedMentions,
				setDeletedMentions,
				setAddedMentions,
				userId,
				unrestrictedMentions,
				parentSpaceRestrictedMentions,
				pageRestrictedMentions,
				unrestrictedTasks,
				addedTasks,
				deletedTasks,
				setAddedTasks,
				setDeletedTasks,
				pageRestrictedTasks,
				parentSpaceRestrictedTasks,
				setMentionsAdded,
				setTasksAdded,
			});
			setMentionChanges([]);
		}
		// We only need to trigger the handler function when mentionChanges gets updated
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [mentionChanges]);

	const editorAPIRef = useRef<FullPageEditorPresetAPINext | null | undefined>(null);

	const { isPluginEnabled: isLoomPluginEnabled, pluginOptions: loomPluginOptions } =
		useLoomPluginOptions({
			EditorButtonComponent: EditorLoomButton,
			editorAPIRef,
			// [ED-27497] IMPORTANT: If you change the code below, please also update the
			// platform/packages/editor/editor-presets-confluence/src/presets/full-page/loomPluginOptions.ts
			...(fg('confluence_frontend_content_wrapper')
				? { editorShouldShowToolbarLoomButton: false }
				: {}),
		});

	const { config: editorEngagementPlatformPluginConfig } = useEditorEngagementPlatform();

	const editorPluginAIDefinitionsEnabled = useEditorPluginAIDefinitionsEnabled();
	const editorPluginAIDefinitionsConfig = useEditorPluginAIDefinitionsConfig();

	const editorPluginAIProactiveEnabled = useEditorPluginAIProactiveEnabled({
		aiGlobalOptInStatus: props.aiGlobalOptIn.status,
	});

	const editorPluginAIProactiveConfig = useEditorPluginAIProactiveConfig({
		aiGlobalOptIn: props.aiGlobalOptIn,
		editorPluginAIFeedbackHandlers: props.editorPluginAIFeedbackHandlers,
	});

	const selectionExtensionPluginOptions = useSelectionExtensionPluginOptions({
		isSidePanelOpenRef,
		rightSidebarContextRef,
		pageModeRef,
		hasEditPermissionRef,
		contentId: props.contentId,
		editorAPIRef,
	});

	const limitedModePluginOptions = {
		contentId: props.contentId,
		showFlag: props.flags.showFlag,
	} as LimitedModePluginOptions;

	const { isEligibleCreateNewWhiteboard } = useIsEligibleCreateNewWhiteboard();

	const intl = useIntl();

	const emojiNodeDataProvider = useEmojiNodeDataProvider();

	const presetFunction = useCallback(() => {
		const {
			__livePage,
			__rendererExtensionOptions,
			allowFindReplace,
			annotationProviders,
			appearance,
			autoformattingProvider,
			editorPluginAIOptions,
			collabEdit,
			contextIdentifierProvider,
			elementBrowser,
			emojiProvider,
			extensionHandlers,
			getExtensionHeight,
			getHeadingLinkPath,
			hasRequestedEditPermission,
			isAIEnabledForContent,
			linking,
			media,
			mention,
			mentionProvider,
			quickInsert,
			referentialityContext,
			requestToEditContent,
			taskDecisionProvider,
			viewMode,
			disabled,
			blockLinkHashPrefix,
			showDiffPluginParams,
			syncedBlockProvider,
			disableFloatingToolbar,
			disableToolbar,
		} = props;

		// IMPORTANT: Don't add spread operator (`...props`) here because it will make impossible to
		//            understand which props are used in the preset.
		const presetOptions: CreateFullPageEditorPresetProps = {
			__livePage,
			__rendererExtensionOptions,
			allowFindReplace,
			annotationProviders,
			appearance,
			autoformattingProvider,
			collabEdit,
			contextIdentifierProvider,
			contextPanelOptions: {
				objectSideBar: {
					showPanel: showObjectSidebar,
					closePanel: hideObjectSidebar,
					closePanelById: hideObjectSidebarByPanelId,
				},
			},
			editorEngagementPlatformPluginConfig,
			editorPluginAIDefinitionsConfig,
			editorPluginAIDefinitionsEnabled,
			editorPluginAIOptions,
			editorPluginAIProactiveConfig,
			editorPluginAIProactiveEnabled,
			elementBrowser,
			emojiProvider,
			emojiNodeDataProvider,
			extensionHandlers,
			flags: props.flags,
			getExtensionHeight,
			getHeadingLinkPath,
			handleMentionsChanged: (changes: MentionType[]) => {
				if (changes.length) {
					setMentionChanges(changes);
				}
			},
			hasEditPermission: props.hasEditPermission,
			hasRequestedEditPermission,
			intl,
			isAIEnabledForContent,
			isCompanyHub: props.isCompanyHub || false,
			isDesktop,
			isEligibleCreateNewWhiteboard,
			isLoomPluginEnabled,
			limitedModePluginOptions,
			linking,
			loomPluginOptions,
			media,
			mention,
			mentionProvider,
			prevAppearance: previousEditorProps?.appearance,
			quickInsert,
			referentialityContext,
			requestToEditContent,
			selectionExtensionPluginOptions,
			taskDecisionProvider,
			userPreferencesProvider: confluenceEditorPreferencesProvider,
			userPreferencesProviderNext: editorPreferencesProvider,
			viewMode,
			contextualFormattingEnabled: props.contextualFormattingEnabled,
			disabled,
			blockLinkHashPrefix,
			showDiffPluginParams,
			syncedBlockProvider,
			disableFloatingToolbar,
			disableToolbar,
		};

		return createFullPageEditorPreset(presetOptions);
		// The dependency array should mirror the dependencies we pass to usePreset.
		// Refactor this to only use one usePreset when cleaning up confluence_frontend_transition_preloading_system FG
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.appearance, props.contentId]);

	const { preset: presetStandard, editorApi: editorApiStandard } = usePreset(presetFunction, [
		// WARNING: Adding this dependency array causes the editor plugins to all recreate
		// This can be very expensive. Be very careful adding to this, avoid if possible!
		// Generally we can update props via their plugin APIs (see `placeholder` as an example)
		// update preset based on props change, originally from packages/editor/editor-core/src/create-editor/preset-utils.ts
		// persistScrollGutter not used, allowUndoRedoButtons and sanitizePrivateContent always true
		props.appearance,
	]);
	const { preset: presetSmootherTransitionsFG, editorApi: editorApiSmootherTransitionsFG } =
		usePreset(presetFunction, [props.appearance, props.contentId]);

	const isPresetRerunningOnTransition =
		fg('confluence_frontend_transition_preloading_system') && isLivePage;
	const editorApi = isPresetRerunningOnTransition
		? editorApiSmootherTransitionsFG
		: editorApiStandard;
	const preset = isPresetRerunningOnTransition ? presetSmootherTransitionsFG : presetStandard;

	useEffect(() => {
		editorApi?.core?.actions.execute(
			editorApi?.placeholder?.commands.setPlaceholder(props.placeholder ?? ''),
		);
	}, [editorApi, props.placeholder]);

	useEffect(() => {
		if (fg('confluence_frontend_transition_preloading_system')) {
			editorApi?.media?.actions.setProvider(props.media?.provider);
		}
	}, [editorApi, props.media?.provider]);

	const { setEditorAPI } = props;

	useEffect(() => {
		if (!setEditorAPI) {
			return;
		}
		editorAPIRef.current = editorApi;
		setEditorAPI(editorApi);
	}, [editorApi, setEditorAPI]);

	useEffect(() => {
		if (props.viewMode === undefined) {
			return;
		}
		editorApi?.core?.actions?.execute(
			editorApi?.editorViewMode?.commands?.updateViewMode(props.viewMode),
		);
	}, [props.viewMode, editorApi]);

	useEffect(() => {
		editorApi?.core?.actions?.execute(
			editorApi?.taskDecision?.commands?.updateEditPermission(props.hasEditPermission),
		);
	}, [props.hasEditPermission, editorApi]);

	useEffect(() => {
		editorApi?.core?.actions?.execute(
			editorApi?.taskDecision?.commands?.updateHasRequestedEditPermission(
				props.hasRequestedEditPermission || false,
			),
		);
	}, [props.hasRequestedEditPermission, editorApi]);

	const { enabled, autoHighlightEnabled, enablementSource } = editorPluginAIDefinitionsConfig;
	useEffect(() => {
		if (!fg('confluence_ai_access_policy')) {
			return;
		}
		editorApi?.core?.actions?.execute(
			editorApi?.aiDefinitions?.commands?.updateState({
				enabled,
				autoHighlightEnabled,
				enablementSource,
			}),
		);
	}, [editorApi, enabled, autoHighlightEnabled, enablementSource]);

	const { openLoomRecorder } = useLoomRecorderEntryPoint({
		flags: props.flags,
		entryPointLocation: 'editor-toolbar',
		resetInsertFunctionOnUnmount: true,
	});

	const loomProviderConfig: LoomProviderConfig = useMemo(
		() => ({ openLoomRecorder }),
		[openLoomRecorder],
	);

	useInitializeLoomPlugin({
		loomPlugin: editorApi?.loom as EditorAPI['loom'],
		providerConfig: loomProviderConfig,
	});

	return preset;
}
